import React, { useState, useEffect } from 'react';
import { 
  Zap, Play, Pause, Clock, Users, Mail, Tag, Plus, X, Check, 
  ChevronRight, Loader2, RefreshCw, AlertCircle, Settings,
  BarChart3, Activity, Archive, Eye, Edit2, Trash2, GitBranch,
  CheckCircle, XCircle, Timer, ArrowRight, Filter
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import api from '../../lib/api';
import { toast } from 'sonner';

const AdminAutomations = () => {
  const [loading, setLoading] = useState(true);
  const [automations, setAutomations] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [showLogsPanel, setShowLogsPanel] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('list');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [autoRes, statsRes] = await Promise.all([
        api.get('/marketing/automations'),
        api.get('/marketing/automations/stats')
      ]);
      setAutomations(autoRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAutomationDetail = async (automationId) => {
    try {
      const res = await api.get(`/marketing/automations/${automationId}`);
      setSelectedAutomation(res.data);
    } catch (error) {
      toast.error('Error al cargar automatización');
    }
  };

  const fetchRecentLogs = async () => {
    try {
      const res = await api.get('/marketing/automations/logs/recent?limit=50');
      setRecentLogs(res.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const updateStatus = async (automationId, newStatus) => {
    try {
      await api.put(`/marketing/automations/${automationId}/status?status=${newStatus}`);
      toast.success(`Automatización ${newStatus === 'active' ? 'activada' : newStatus === 'paused' ? 'pausada' : 'archivada'}`);
      fetchData();
      if (selectedAutomation?.id === automationId) {
        fetchAutomationDetail(automationId);
      }
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const createEbookAutomation = async () => {
    try {
      const res = await api.post('/marketing/automations/create-ebook-default');
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Error al crear automatización');
    }
  };

  const processPendingRuns = async () => {
    try {
      const res = await api.post('/marketing/automations/process-pending');
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Error al procesar runs pendientes');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-ES', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-100 text-emerald-700',
      draft: 'bg-slate-100 text-slate-600',
      paused: 'bg-amber-100 text-amber-700',
      archived: 'bg-red-100 text-red-600',
      running: 'bg-blue-100 text-blue-700',
      completed: 'bg-emerald-100 text-emerald-700',
      failed: 'bg-red-100 text-red-700',
      stopped: 'bg-slate-100 text-slate-600'
    };
    return styles[status] || 'bg-slate-100 text-slate-600';
  };

  const getNodeIcon = (type) => {
    switch (type) {
      case 'send_email': return <Mail className="w-4 h-4 text-blue-500" />;
      case 'wait': return <Timer className="w-4 h-4 text-amber-500" />;
      case 'add_tag': return <Tag className="w-4 h-4 text-emerald-500" />;
      case 'remove_tag': return <Tag className="w-4 h-4 text-red-500" />;
      case 'add_score': return <BarChart3 className="w-4 h-4 text-purple-500" />;
      case 'if_else': return <GitBranch className="w-4 h-4 text-indigo-500" />;
      case 'set_lifecycle': return <Users className="w-4 h-4 text-pink-500" />;
      case 'stop': return <XCircle className="w-4 h-4 text-slate-400" />;
      default: return <Zap className="w-4 h-4 text-slate-400" />;
    }
  };

  const getActionBadge = (action) => {
    const styles = {
      executed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      executing: 'bg-blue-50 text-blue-700 border-blue-200',
      waiting: 'bg-amber-50 text-amber-700 border-amber-200',
      evaluated: 'bg-purple-50 text-purple-700 border-purple-200',
      completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      error: 'bg-red-50 text-red-700 border-red-200',
      skipped: 'bg-slate-50 text-slate-600 border-slate-200'
    };
    return styles[action] || 'bg-slate-50 text-slate-600 border-slate-200';
  };

  if (loading) {
    return (
      <div className="p-6 min-h-[40vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="automations-tab">
      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-slate-100 pb-2">
        <button
          onClick={() => setActiveSubTab('list')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeSubTab === 'list'
              ? 'bg-purple-500 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <Zap className="w-4 h-4" />
          Flujos
        </button>
        <button
          onClick={() => { setActiveSubTab('logs'); fetchRecentLogs(); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeSubTab === 'logs'
              ? 'bg-purple-500 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <Activity className="w-4 h-4" />
          Historial
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-400 uppercase">Flujos Activos</span>
              <Zap className="w-4 h-4 text-purple-500" />
            </div>
            <p className="text-2xl font-semibold text-slate-900">{stats.automations?.active || 0}</p>
            <p className="text-xs text-slate-400">de {stats.automations?.total || 0} totales</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-400 uppercase">En Ejecución</span>
              <Play className="w-4 h-4 text-blue-500" />
            </div>
            <p className="text-2xl font-semibold text-blue-600">{stats.runs?.running || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-400 uppercase">Completados</span>
              <CheckCircle className="w-4 h-4 text-emerald-500" />
            </div>
            <p className="text-2xl font-semibold text-emerald-600">{stats.runs?.completed || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-400 uppercase">Errores</span>
              <AlertCircle className="w-4 h-4 text-red-500" />
            </div>
            <p className="text-2xl font-semibold text-red-600">{stats.runs?.failed || 0}</p>
          </div>
        </div>
      )}

      {/* Flujos List */}
      {activeSubTab === 'list' && (
        <div className="space-y-4">
          {/* Actions */}
          <div className="flex justify-between items-center">
            <h2 className="font-semibold text-slate-900">Flujos de Automatización</h2>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={processPendingRuns}
                title="Procesar runs con waits pendientes"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Procesar Pendientes
              </Button>
              <Button 
                size="sm"
                onClick={createEbookAutomation}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear Flujo Ebook
              </Button>
            </div>
          </div>

          {/* Automations Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {automations.map(auto => (
              <div 
                key={auto.id} 
                className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 hover:border-purple-200 transition-all cursor-pointer"
                onClick={() => fetchAutomationDetail(auto.id)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-slate-900">{auto.name}</h3>
                    <p className="text-xs text-slate-500 mt-1">{auto.description || 'Sin descripción'}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(auto.status)}`}>
                    {auto.status}
                  </span>
                </div>

                <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                  <span className="flex items-center gap-1">
                    <Zap className="w-3.5 h-3.5" />
                    {auto.trigger?.event_name || 'manual'}
                  </span>
                  <span className="flex items-center gap-1">
                    <GitBranch className="w-3.5 h-3.5" />
                    {auto.nodes?.length || 0} nodos
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    {auto.run_count || 0} runs
                  </span>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <span className="text-xs text-slate-400">
                    Último: {formatDate(auto.last_run_at)}
                  </span>
                  <div className="flex gap-1">
                    {auto.status === 'active' ? (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); updateStatus(auto.id, 'paused'); }}
                      >
                        <Pause className="w-4 h-4 text-amber-500" />
                      </Button>
                    ) : auto.status !== 'archived' ? (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); updateStatus(auto.id, 'active'); }}
                      >
                        <Play className="w-4 h-4 text-emerald-500" />
                      </Button>
                    ) : null}
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); fetchAutomationDetail(auto.id); }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {automations.length === 0 && (
              <div className="col-span-2 text-center py-12 bg-slate-50 rounded-xl">
                <Zap className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="font-medium text-slate-600 mb-2">Sin automatizaciones</h3>
                <p className="text-sm text-slate-400 mb-4">Crea tu primera automatización para empezar</p>
                <Button onClick={createEbookAutomation} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Flujo Ebook
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Logs List */}
      {activeSubTab === 'logs' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="font-semibold text-slate-900">Historial de Ejecuciones</h2>
            <Button variant="outline" size="sm" onClick={fetchRecentLogs}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100 sticky top-0">
                  <tr>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Fecha</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Nodo</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Acción</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Detalles</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recentLogs.map(log => (
                    <tr key={log.id} className="hover:bg-slate-50">
                      <td className="p-3 text-sm text-slate-500">
                        {formatDate(log.created_at)}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {getNodeIcon(log.node_type)}
                          <span className="text-sm font-medium text-slate-700">{log.node_type}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded border ${getActionBadge(log.action)}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="p-3 text-xs text-slate-500 max-w-xs truncate">
                        {JSON.stringify(log.details || {}).substring(0, 100)}
                      </td>
                    </tr>
                  ))}
                  {recentLogs.length === 0 && (
                    <tr>
                      <td colSpan={4} className="p-8 text-center text-slate-400">
                        No hay logs de ejecución
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Automation Detail Modal */}
      {selectedAutomation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100 sticky top-0 bg-white">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">{selectedAutomation.name}</h3>
                  <p className="text-sm text-slate-500 mt-1">{selectedAutomation.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(selectedAutomation.status)}`}>
                    {selectedAutomation.status}
                  </span>
                  <button onClick={() => setSelectedAutomation(null)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Trigger Info */}
              <div className="bg-purple-50 rounded-xl p-4">
                <h4 className="font-medium text-purple-900 mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Trigger
                </h4>
                <div className="text-sm text-purple-700">
                  <p><strong>Tipo:</strong> {selectedAutomation.trigger?.type}</p>
                  <p><strong>Evento:</strong> {selectedAutomation.trigger?.event_name}</p>
                  {selectedAutomation.trigger?.filters && Object.keys(selectedAutomation.trigger.filters).length > 0 && (
                    <p><strong>Filtros:</strong> {JSON.stringify(selectedAutomation.trigger.filters)}</p>
                  )}
                </div>
              </div>

              {/* Flow Nodes */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <GitBranch className="w-4 h-4" />
                  Flujo ({selectedAutomation.nodes?.length || 0} nodos)
                </h4>
                <div className="space-y-2">
                  {(selectedAutomation.nodes || []).map((node, i) => (
                    <div key={node.id} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-500">
                        {i + 1}
                      </div>
                      <div className="flex-1 flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        {getNodeIcon(node.type)}
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-700">{node.type}</p>
                          <p className="text-xs text-slate-500">{JSON.stringify(node.config || {})}</p>
                        </div>
                      </div>
                      {i < (selectedAutomation.nodes?.length || 0) - 1 && (
                        <ArrowRight className="w-4 h-4 text-slate-300" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Runs */}
              {selectedAutomation.recent_runs && selectedAutomation.recent_runs.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Ejecuciones Recientes
                  </h4>
                  <div className="space-y-2">
                    {selectedAutomation.recent_runs.slice(0, 10).map(run => (
                      <div key={run.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className={`w-2 h-2 rounded-full ${
                            run.status === 'completed' ? 'bg-emerald-500' :
                            run.status === 'running' ? 'bg-blue-500' :
                            run.status === 'failed' ? 'bg-red-500' : 'bg-slate-400'
                          }`} />
                          <div>
                            <p className="text-sm font-medium text-slate-700">
                              {run.contact?.email || run.contact_id}
                            </p>
                            <p className="text-xs text-slate-400">{formatDate(run.started_at)}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(run.status)}`}>
                          {run.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-slate-100">
                {selectedAutomation.status === 'active' ? (
                  <Button 
                    variant="outline" 
                    onClick={() => updateStatus(selectedAutomation.id, 'paused')}
                    className="flex-1"
                  >
                    <Pause className="w-4 h-4 mr-2" />
                    Pausar
                  </Button>
                ) : selectedAutomation.status !== 'archived' && (
                  <Button 
                    onClick={() => updateStatus(selectedAutomation.id, 'active')}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Activar
                  </Button>
                )}
                {selectedAutomation.status !== 'archived' && (
                  <Button 
                    variant="outline"
                    onClick={() => updateStatus(selectedAutomation.id, 'archived')}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Archive className="w-4 h-4 mr-2" />
                    Archivar
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAutomations;
